import React, { useState } from 'react';
import MuiCardBase, { MuiCardCommonProps } from '../MuiCardBase/MuiCardBase';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../../constants';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../reducers/user.selectors';
import { IconName } from '../../../../utils/getMuiIcon';
import { Alert } from '../../../NotificationAlert/NotificationAlert';
import CustomBanner from '../../../CustomBanner/CustomBanner';
import SubscribeIcon from '../../../../assets/subscription/subscribe.svg';
import {
  getUpgradeBannerProps,
  PremiumBenefits,
} from '../../../ReaderBannerContainer/utils/upgradeBannerUtils';
import Benefits from '../../../PricingPackage/Benefits';
import useRouter from '../../../../utils/useRouter';
import { useAmplitudeExperiment } from '../../../../utils/ampli';
import config from 'config';

export interface ProductMuiCardProps extends MuiCardCommonProps {
  numberOfAttachments: number,
  numberOfMindItems: number,
  isNewAddMenuButtonMenu?: boolean;
  onShowNotification?: (alert: Alert) => void;
}

const ProductMuiCard: React.FC<ProductMuiCardProps> = (props) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const { router } = useRouter();
  const [isBannerOpen, setIsBannerOpen] = useState(false);
  const {
    isFirstElement = false,
    numberOfMindItems,
    numberOfAttachments,
    onShowNotification,
    bestMatchMindItemId,
    mindItemPageNumber,
    ...restProps
  } = props;

  const {
    variant: bestMatchVariant,
    isReady: isBestMatchReady
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.bestMatchDeepLink);
  const isBestMatchEnabled = isBestMatchReady && bestMatchVariant === 'on';

  const chipText = `${numberOfMindItems} ${numberOfMindItems > 1 ? t('Units') : t('Unit')}`;
  const attachmentsPageLink = `/${routes.productAttachmentsPage.root}/${props.id}`;
  const productPageLink = `/${routes.productPage.root}/${props.id}`;
  const hasBestMatch = isBestMatchEnabled
    && !!bestMatchMindItemId && mindItemPageNumber !== undefined && mindItemPageNumber > 0;
  const attachmentsLink = {
    to: user?.hasDocumentLevelAccess ? attachmentsPageLink : productPageLink,
    label: `${numberOfAttachments} ${numberOfAttachments === 1 ? t('Attachment') : t('Attachments')}`,
    icon: 'paperclip' as IconName,
    hasTooltip: false,
  };
  const readerLink = hasBestMatch ? {
    to: `/${routes.reader.root}/${bestMatchMindItemId}?page=${mindItemPageNumber}`,
    label: t('Open relevant unit'),
    icon: 'docText' as IconName,
    hasTooltip: false,
  } : undefined;

  const customLinkFunction = () => {
    setIsBannerOpen(true);
  };

  if (!user) {
    return <MuiCardBase
      {...restProps}
      isFirstElement={isFirstElement}
      contentType='product'
      chipText={chipText}
      link={numberOfAttachments > 0 ? attachmentsLink : undefined}
      linkFunction={customLinkFunction}
      secondaryLink={readerLink}
      onShowNotification={onShowNotification}
    />;
  }

  const upgradeBannerProps = getUpgradeBannerProps(user, router, SubscribeIcon, t);
  const bannerProps = {
    ...upgradeBannerProps,
    additionalInfo: <Benefits benefit={PremiumBenefits} />,
  };

  return (
    <>
      <MuiCardBase
        {...restProps}
        isFirstElement={isFirstElement}
        contentType='product'
        chipText={chipText}
        link={numberOfAttachments > 0 ? attachmentsLink : undefined}
        linkFunction={user?.hasDocumentLevelAccess ? undefined : customLinkFunction}
        secondaryLink={readerLink}
        onShowNotification={onShowNotification}
      />
      <CustomBanner
        open={isBannerOpen}
        onClose={() => setIsBannerOpen(false)}
        {...bannerProps}
      />
    </>
  );
};

export default ProductMuiCard;
